import { useEffect, useRef } from 'react';
import { useAuthStore } from '@/stores/useAuthStore';
import { usePdfPrefsStore, type PdfPrefs } from '@/stores/usePdfPrefsStore';
import { setSignerIdentityLine } from '@/utils/pdfBrand';
import { isMaieShell } from '@/config/maieShell';
import { pdfPrefsApi } from '@/services/pdfPrefsApi';

/**
 * Sync per-doctor PDF preferences between the backend record
 * (`users.preferences.pdfPrefs`) and the per-device `maie-pdf-prefs`
 * localStorage store. Same two-phase shape as useVoicePrefsSync:
 *
 *   - EVERY post-auth mount: GET and REPLACE the local store from the server.
 *     The server is the source of truth so every origin (app.maie.to + each
 *     tenant subdomain) renders 1:1 — a change saved on one origin shows up on
 *     the next load of any other. (We previously short-circuited this with a
 *     per-origin localStorage "hydrated" marker, which is exactly what let one
 *     origin keep serving its own stale cache forever.)
 *   - On local change: debounced PUT.
 *   - On `pagehide`: flush any pending PUT via keepalive fetch — so a refresh
 *     between a change and the debounced PUT persists before the next mount's
 *     pull, and the pull can't clobber the just-made choice.
 *
 * Mounted once at MainLayout (post-auth root). No-op pre-auth.
 */

const PUSH_DEBOUNCE_MS = 400;
const FIELDS = ['template', 'accentColor', 'signatureImage', 'credentialsLine'] as const;

const snapshotOf = (s: PdfPrefs): PdfPrefs => ({
  template: s.template,
  accentColor: s.accentColor,
  signatureImage: s.signatureImage,
  credentialsLine: s.credentialsLine,
});

export function usePdfPrefsSync(): void {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const userId = useAuthStore((s) => s.user?.id);

  const lastPushedRef = useRef<string | null>(null);
  const hydratedForUserRef = useRef<string | null>(null);

  // Pull + REPLACE from the server once per authenticated mount. A fresh page
  // load on any origin re-pulls, so the server stays authoritative and all
  // origins render 1:1. The per-mount ref keeps it to a single fetch (not
  // once-per-render); it resets on unmount so the next page load re-pulls.
  useEffect(() => {
    if (!isAuthenticated || !userId) {
      hydratedForUserRef.current = null;
      return;
    }
    if (hydratedForUserRef.current === userId) return;
    hydratedForUserRef.current = userId;

    let cancelled = false;
    (async () => {
      try {
        const prefs = await pdfPrefsApi.get();
        if (cancelled) return;
        // Build the exact server-side state. Absent/empty fields mean "use
        // the clinic default" — hydratePdfPrefs REPLACES the whole object
        // (clearing anything the server no longer has), so a value cleared on
        // another origin actually disappears here too.
        const next: PdfPrefs = {};
        for (const key of FIELDS) {
          const value = prefs[key];
          if (typeof value === 'string' && value.length > 0) {
            next[key] = value as never;
          }
        }
        // Pre-seed lastPushed to the incoming state so applying it doesn't
        // echo a redundant PUT back to the server.
        // The server's credentials line is a design-shell fallback; the old
        // shell prints only what the doctor wrote.
        setSignerIdentityLine(isMaieShell() ? prefs.identityLine : null);
        lastPushedRef.current = JSON.stringify(snapshotOf(next));
        usePdfPrefsStore.getState().hydratePdfPrefs(next);
      } catch {
        // Swallow — local value still drives the UI; retry on next mount.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, userId]);

  // Push local changes back (debounced) + flush on pagehide.
  useEffect(() => {
    if (!isAuthenticated || !userId) return;

    let timer: ReturnType<typeof setTimeout> | null = null;
    let lastSeen: string | null = null;
    let pendingSnapshot: PdfPrefs | null = null;
    let pendingSerialized: string | null = null;

    const flushPending = () => {
      if (timer) {
        clearTimeout(timer);
        timer = null;
      }
      if (!pendingSnapshot || !pendingSerialized) return;
      const snapshot = pendingSnapshot;
      const serialized = pendingSerialized;
      pendingSnapshot = null;
      pendingSerialized = null;

      const apiBase =
        (import.meta.env.VITE_API_BASE_URL as string | undefined) ||
        'http://localhost:3000';
      const token = localStorage.getItem('accessToken');
      try {
        void fetch(`${apiBase.replace(/\/$/, '')}/users/me/pdf-prefs`, {
          method: 'PUT',
          keepalive: true,
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify(snapshot),
        });
        lastPushedRef.current = serialized;
      } catch {
        // best-effort under unload
      }
    };

    const unsub = usePdfPrefsStore.subscribe((state) => {
      const snapshot = snapshotOf(state);
      const serialized = JSON.stringify(snapshot);
      if (lastSeen === serialized) return;
      lastSeen = serialized;
      if (serialized === lastPushedRef.current) return;

      pendingSnapshot = snapshot;
      pendingSerialized = serialized;

      if (timer) clearTimeout(timer);
      timer = setTimeout(async () => {
        timer = null;
        const toSend = pendingSnapshot;
        const toSendSerialized = pendingSerialized;
        pendingSnapshot = null;
        pendingSerialized = null;
        if (!toSend || !toSendSerialized) return;
        try {
          // Send explicit empty (null for the enum'd template, '' for the
          // strings) so cleared fields reset to the clinic default server-side.
          await pdfPrefsApi.put({
            template: toSend.template ?? null,
            accentColor: toSend.accentColor ?? '',
            signatureImage: toSend.signatureImage ?? '',
            credentialsLine: toSend.credentialsLine ?? '',
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
          } as any);
          lastPushedRef.current = toSendSerialized;
        } catch {
          // retry on next change
        }
      }, PUSH_DEBOUNCE_MS);
    });

    const handlePageHide = () => flushPending();
    window.addEventListener('pagehide', handlePageHide);

    return () => {
      window.removeEventListener('pagehide', handlePageHide);
      if (timer) clearTimeout(timer);
      unsub();
    };
  }, [isAuthenticated, userId]);
}
